import { EventType, type EventMessage, type IPublicClientApplication } from '@azure/msal-browser';

/**
 * Startup sequence modelled on rnav apps/frontend/src/main.tsx: finish a pending
 * redirect, pick the active account, reload when another tab adds, removes or
 * switches the signed-in account, and send a user with no account to Entra
 * sign-in before anything renders.
 *
 * LOGIN_SUCCESS is not a cross-tab event - it only fires in the tab that performed
 * the login - and in this redirect flow it fires inside handleRedirectPromise()
 * above, before any callback can be registered, so it would never reach a handler
 * here. enableAccountStorageEvents() is what actually delivers ACCOUNT_ADDED /
 * ACCOUNT_REMOVED / ACTIVE_ACCOUNT_CHANGED across tabs; reloading is simpler and
 * safer than trying to resync the in-memory session in place.
 */
export async function bootstrapMsal(
  pca: IPublicClientApplication,
  loginScopes: string[],
  onAccountChangedElsewhere: () => void = () => window.location.reload(),
): Promise<'ready' | 'redirecting'> {
  await pca.initialize();

  const result = await pca.handleRedirectPromise();
  if (result?.account) {
    pca.setActiveAccount(result.account);
  }
  if (!pca.getActiveAccount()) {
    const [first] = pca.getAllAccounts();
    if (first) pca.setActiveAccount(first);
  }

  pca.enableAccountStorageEvents();
  pca.addEventCallback((event: EventMessage) => {
    if (
      event.eventType === EventType.ACCOUNT_ADDED ||
      event.eventType === EventType.ACCOUNT_REMOVED ||
      event.eventType === EventType.ACTIVE_ACCOUNT_CHANGED
    ) {
      onAccountChangedElsewhere();
    }
  });

  if (!pca.getActiveAccount()) {
    await pca.loginRedirect({ scopes: loginScopes });
    return 'redirecting';
  }
  return 'ready';
}
